import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";

type OfflineMessageFormProps = {
  message: string; active: boolean; day: string;
  onChange: (patch: { message?: string; active?: boolean }, merge?: boolean) => void;
  onBlur?: () => void; dragHandle?: ReactNode; className?: string;
};

export function OfflineMessageForm({ message, active, day, onChange, onBlur, dragHandle, className }: OfflineMessageFormProps) {
  const { t } = useTranslation();

  return (
    <div className={cn(
      "relative flex min-w-0 items-center gap-2 border-t border-neutral-200 pt-2",
      className,
    )}>
      {dragHandle}
      <Button type="button" size="xs" variant="ghost" tabIndex={-1} title={t("schedule.offlineHelp")}
        aria-label={`${day}, ${t("schedule.offlineToggle")}`} aria-pressed={active}
        onClick={() => onChange({ active: !active })}
        className={cn("h-7 w-[94px] shrink-0 justify-start gap-1.5 rounded border border-neutral-200 bg-neutral-50 px-2 text-[10px]", active && "bg-emerald-50 text-emerald-700 hover:bg-emerald-100")}>
        <span aria-hidden="true" className={cn("size-1.5 rounded-full", active ? "bg-emerald-500" : "bg-neutral-300")} />{t("schedule.offline")}
      </Button>
      <Input value={message} className={cn("h-7 min-w-0 flex-1 rounded bg-white px-2 text-xs shadow-none transition-opacity", !active && "opacity-60 focus:opacity-100")}
        aria-label={`${day}, ${t("schedule.offlineMessage")}`} placeholder={t("art.offline")}
        title={t("schedule.offlineHelp")} onBlur={onBlur}
        onChange={event => onChange({ message: event.target.value, ...(event.target.value.length > 0 ? { active: true } : {}) }, true)} />
      <Button type="button" variant="ghost" size="icon-xs" className="h-7 w-6 shrink-0 text-neutral-400 hover:bg-red-50 hover:text-red-600"
        aria-label={`${day}, ${t("schedule.clearOfflineMessage")}`} onClick={() => onChange({ message: "" })}>
        <Trash2 className="size-3.5" />
      </Button>
    </div>
  );
}
